import React from 'react';
import { 
  SMASettings, 
  RSISettings, 
  MACDSettings, 
  VolumeHistogramSettings, 
  BuySellFrequencySettings, 
  StochasticSettings 
} from './settings';
import { SHIO_INFO } from './DailyShio';
import { ELEMENT_INFO } from './DailyElement';

export interface IndicatorEditorProps {
  indicator: any;
  onSave: (indicator: any) => void;
  onCancel: () => void;
  volumeHistogramSettings: any;
  setVolumeHistogramSettings: any;
}

export function IndicatorEditor({ 
  indicator, 
  onSave, 
  onCancel,
  volumeHistogramSettings,
  setVolumeHistogramSettings
}: IndicatorEditorProps) {
  const [editedIndicator, setEditedIndicator] = React.useState<any>(indicator);

  const handleSave = () => {
    onSave(editedIndicator);
  };

  const renderIndicatorSettings = () => {
    switch (editedIndicator.type) {
      case 'ma':
        return <SMASettings editedIndicator={editedIndicator} setEditedIndicator={setEditedIndicator} />;

      case 'rsi':
        return <RSISettings editedIndicator={editedIndicator} setEditedIndicator={setEditedIndicator} />;

      case 'macd':
        return <MACDSettings editedIndicator={editedIndicator} setEditedIndicator={setEditedIndicator} />;

      case 'volume_histogram':
        return (
          <VolumeHistogramSettings 
            editedIndicator={editedIndicator} 
            setEditedIndicator={setEditedIndicator}
            volumeHistogramSettings={volumeHistogramSettings}
            setVolumeHistogramSettings={setVolumeHistogramSettings}
          />
        );

      case 'buy_sell_frequency':
        return <BuySellFrequencySettings editedIndicator={editedIndicator} setEditedIndicator={setEditedIndicator} />;

      case 'stochastic':
        return <StochasticSettings editedIndicator={editedIndicator} setEditedIndicator={setEditedIndicator} />;

      case 'bazi_indicator':
      case 'bazi_daily':
      case 'bazi_monthly':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-2">BaZi Element Colors</label>
              <div className="space-y-2 max-h-60 overflow-y-auto border border-border rounded-md p-2">
                {ELEMENT_INFO.map((item) => (
                  <div key={item.name} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span style={{ fontSize: '16px' }}>{item.emoji}</span>
                      <span className="text-sm text-foreground">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded border border-border"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-xs text-muted-foreground font-mono">{item.color}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground mt-2 space-y-1">
                <p className="font-medium text-foreground">Struktur 2 Kotak Bertumpuk:</p>
                <p>• <strong>Kotak Atas (0.5 - 1.0):</strong> Element Utama (Heavenly Stem / Day Element)</p>
                <p>• <strong>Kotak Bawah (0.0 - 0.5):</strong> Element Shio (Earthly Branch, misal: Monyet Metal = Abu-abu)</p>
              </div>
            </div>
          </div>
        );

      case 'daily_shio':
      case 'daily_element':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-2">Day Limit</label>
              <input
                type="number"
                min="1"
                max="365"
                value={editedIndicator.dayLimit || 30}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setEditedIndicator((prev: any) => ({ 
                    ...prev, 
                    dayLimit: isNaN(value) ? 30 : Math.max(1, Math.min(365, value))
                  }));
                }}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Number of emoji/shape markers to display (1-365). Default: 30 markers
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Display Mode</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="displayMode"
                    value="emoji"
                    checked={(editedIndicator.displayMode || 'emoji') === 'emoji'}
                    onChange={() => {
                      setEditedIndicator((prev: any) => ({ 
                        ...prev, 
                        displayMode: 'emoji' as const
                      }));
                    }}
                    className="w-4 h-4 text-primary"
                  />
                  <span className="text-sm">Emoji</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="displayMode"
                    value="shape"
                    checked={editedIndicator.displayMode === 'shape'}
                    onChange={() => {
                      setEditedIndicator((prev: any) => ({ 
                        ...prev, 
                        displayMode: 'shape' as const,
                        shape: prev.shape || 'circle',
                        shapeColors: prev.shapeColors || {}
                      }));
                    }}
                    className="w-4 h-4 text-primary"
                  />
                  <span className="text-sm">Shape</span>
                </label>
              </div>
            </div>

            {editedIndicator.displayMode === 'shape' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Shape</label>
                  <select
                    value={editedIndicator.shape || 'circle'}
                    onChange={(e) => {
                      setEditedIndicator((prev: any) => ({ 
                        ...prev, 
                        shape: e.target.value as 'circle' | 'square' | 'arrowUp' | 'arrowDown'
                      }));
                    }}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  >
                    <option value="circle">Circle</option>
                    <option value="square">Square</option>
                    <option value="arrowUp">Arrow Up</option>
                    <option value="arrowDown">Arrow Down</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {editedIndicator.type === 'daily_shio' ? 'Shio Colors' : 'Element Colors'}
                  </label>
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-border rounded-md p-2">
                    {(editedIndicator.type === 'daily_shio' ? SHIO_INFO : ELEMENT_INFO).map((item) => {
                      const currentColor = editedIndicator.shapeColors?.[item.name] || item.color;
                      return (
                        <div key={item.name} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1">
                            <span style={{ fontSize: '16px' }}>{item.emoji}</span>
                            <span className="text-sm text-foreground">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={currentColor}
                              onChange={(e) => {
                                setEditedIndicator((prev: any) => ({ 
                                  ...prev, 
                                  shapeColors: {
                                    ...(prev.shapeColors || {}),
                                    [item.name]: e.target.value
                                  }
                                }));
                              }}
                              className="h-8 w-16 border border-border rounded-md cursor-pointer"
                            />
                            <input
                              type="text"
                              value={currentColor}
                              onChange={(e) => {
                                setEditedIndicator((prev: any) => ({ 
                                  ...prev, 
                                  shapeColors: {
                                    ...(prev.shapeColors || {}),
                                    [item.name]: e.target.value
                                  }
                                }));
                              }}
                              placeholder={item.color}
                              className="w-20 px-2 py-1 text-xs border border-border rounded-md bg-background text-foreground"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Custom colors for each {editedIndicator.type === 'daily_shio' ? 'shio' : 'element'} shape markers
                  </p>
                </div>
              </>
            )}
          </div>
        );

      case 'vertical_line_15d':
        const formattedDateValue = editedIndicator.baseTime
          ? new Date(editedIndicator.baseTime * 1000).toISOString().split('T')[0]
          : '';

        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Line Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={editedIndicator.color || '#8b5cf6'}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    setEditedIndicator((prev: any) => ({ ...prev, color: newColor }));
                    try {
                      localStorage.setItem('last_vertical_line_color', newColor);
                    } catch {}
                  }}
                  className="w-10 h-8 border border-border rounded cursor-pointer"
                />
                <span className="text-xs font-mono text-muted-foreground">{editedIndicator.color || '#8b5cf6'}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">Opacity</label>
                <span className="text-xs font-mono text-muted-foreground font-semibold">
                  {Math.round((editedIndicator.opacity ?? 0.85) * 100)}%
                </span>
              </div>
              <input
                type="range"
                min="0.1"
                max="1.0"
                step="0.05"
                value={editedIndicator.opacity ?? 0.85}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setEditedIndicator((prev: any) => ({ ...prev, opacity: val }));
                }}
                className="w-full accent-primary cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cycle Direction</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'forward', label: 'Forward (+)' },
                  { value: 'backward', label: 'Backward (-)' },
                  { value: 'both', label: 'Both (±)' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEditedIndicator((prev: any) => ({ ...prev, direction: opt.value }))}
                    className={`px-2 py-1.5 text-xs rounded border text-center font-medium transition-colors ${
                      (editedIndicator.direction || 'forward') === opt.value
                        ? 'bg-primary text-primary-foreground border-primary font-bold shadow-sm'
                        : 'bg-background text-foreground border-border hover:bg-accent'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Interval Days</label>
              <input
                type="number"
                min="1"
                max="90"
                value={editedIndicator.intervalDays || 15}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setEditedIndicator((prev: any) => ({
                    ...prev,
                    intervalDays: isNaN(val) ? 15 : Math.max(1, Math.min(90, val))
                  }));
                }}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Default: 15 days</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Total Lines</label>
              <input
                type="number"
                min="1"
                max="30"
                value={editedIndicator.lineCount || 12}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setEditedIndicator((prev: any) => ({
                    ...prev,
                    lineCount: isNaN(val) ? 12 : Math.max(1, Math.min(30, val))
                  }));
                }}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Default: 12 lines</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Base Date (T0)</label>
              <input
                type="date"
                value={formattedDateValue}
                onClick={(e) => {
                  try {
                    (e.currentTarget as HTMLInputElement).showPicker?.();
                  } catch {}
                }}
                onChange={(e) => {
                  const dateVal = e.target.value;
                  if (dateVal) {
                    const timeSec = Math.floor(new Date(dateVal + 'T00:00:00Z').getTime() / 1000);
                    setEditedIndicator((prev: any) => ({
                      ...prev,
                      baseTime: timeSec
                    }));
                  }
                }}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm font-mono cursor-pointer [color-scheme:dark]"
              />
              <p className="text-xs text-muted-foreground mt-1">Select base reference date (T0)</p>
            </div>
          </div>
        );

      default:
        return <div>Unknown indicator type</div>;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Indicator Name</label>
        <input
          type="text"
          value={editedIndicator.name}
          onChange={(e) => setEditedIndicator((prev: any) => ({ ...prev, name: e.target.value }))}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
        />
      </div>

      {renderIndicatorSettings()}

      <div className="flex gap-2 pt-4">
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Save Changes
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-border rounded-md hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
